import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@/shared/services';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';

interface BiomarkerDisplayRulesResponse {
    rules: BiomarkerDisplayRule[];
}

/** Stable reference so a fetch failure (or "not logged in yet") never
 *  produces a new array identity on every render — consumers that memoize
 *  on `rules` would otherwise recompute needlessly. */
const EMPTY: BiomarkerDisplayRule[] = [];

/**
 * Admin-managed band rules, fetched once per session.
 *
 * Failure is non-fatal BY DESIGN: an empty list makes every resolution return
 * null, and classification falls back to lab ranges — the pre-migration
 * behaviour. A rules outage must never blank the dashboard.
 */
export function useDisplayRules(): {rules: BiomarkerDisplayRule[]; isLoading: boolean} {
    const {isLoggedIn} = useAuth();
    const {data, isLoading} = useQuery<BiomarkerDisplayRulesResponse>({
        queryKey: ['biomarker-display-rules'],
        queryFn: () => apiClient.get<BiomarkerDisplayRulesResponse>('/api/biomarker-display-rules'),
        enabled: !!isLoggedIn,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: 1,
    });
    return {rules: data?.rules ?? EMPTY, isLoading};
}
